'use client';

import { differenceInDays, isValid } from 'date-fns';
import { useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { parseSoftSwissFormatDate } from '@/helpers/dateTime';
import { useIsNoDepositUser } from '@/hooks/useIsNoDepositUser';
import type { INDBLandingCode } from '@/modules/promo/hooks';
import { useGetCommonStore } from '@/store';
import {
  useDeleteUserStorageItems,
  useUserStorageItems,
} from '@/store/serverUserStorage';
import { SERVER_USER_STORAGE_IDENTIFIER } from '@/store/serverUserStorage/helpers';

const NDB_LANDING_COUPON_TTL_DAYS = 30;

export const usePrefilledNdbCouponSync = () => {
  const useCommonStore = useGetCommonStore();

  const { bonuses, freespins, hasUserUsedFreespins, hasUserUsedBonuses, user } =
    useCommonStore(
      useShallow((state) => ({
        bonuses: state.bonuses,
        hasUserUsedFreespins: state.hasUserUsedFreespins,
        hasUserUsedBonuses: state.hasUserUsedBonuses,
        user: state.user,
        freespins: state.freespins,
      }))
    );

  const { isNoDepositUser } = useIsNoDepositUser();
  const { deleteUserStorageItems } = useDeleteUserStorageItems();

  const { userStorageItem } = useUserStorageItems<INDBLandingCode>(
    SERVER_USER_STORAGE_IDENTIFIER.NDB_LANDING_COUPON
  );

  const wasNoDepositUserRef = useRef(isNoDepositUser);

  useEffect(() => {
    // FTD: the SignUps group disappeared once the user made their first
    // deposit — the stored NDB coupon no longer applies, drop it.
    if (wasNoDepositUserRef.current && !isNoDepositUser && userStorageItem) {
      deleteUserStorageItems([
        SERVER_USER_STORAGE_IDENTIFIER.NDB_LANDING_COUPON,
      ]).catch((error) => console.log(error));
    }

    wasNoDepositUserRef.current = isNoDepositUser;
  }, [isNoDepositUser, userStorageItem, deleteUserStorageItems]);

  useEffect(() => {
    const isUserHasAnyBonuses =
      !!bonuses?.length ||
      !!freespins?.length ||
      hasUserUsedBonuses ||
      hasUserUsedFreespins;

    const coupon = userStorageItem?.info?.coupon;

    const createdAt = user?.createdAt
      ? parseSoftSwissFormatDate(user.createdAt)
      : undefined;

    const isWithinTtl =
      !!createdAt &&
      isValid(createdAt) &&
      differenceInDays(new Date(), createdAt) <= NDB_LANDING_COUPON_TTL_DAYS;

    const shouldPrefill =
      !isUserHasAnyBonuses &&
      !!coupon &&
      isNoDepositUser &&
      !!user?.confirmedAt &&
      isWithinTtl;

    useCommonStore.setState({
      isPrefilledNdbCoupon: shouldPrefill,
      prefilledNdbCoupon: shouldPrefill ? coupon?.toUpperCase() : undefined,
    });
  }, [
    bonuses,
    freespins,
    hasUserUsedFreespins,
    isNoDepositUser,
    userStorageItem,
    useCommonStore,
    hasUserUsedBonuses,
    user?.confirmedAt,
    user?.createdAt,
  ]);
};
